<template>
  <v-dialog
    :value="value"
    max-width="720"
    persistent
    transition="dialog-bottom-transition"
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-toolbar flat>
        <v-toolbar-title>{{ $t('Modifier certains champs du véhicule') }}</v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <v-form ref="form" v-model="isValid" lazy-validation>
          <!-- Département -->
          <v-autocomplete
            v-model="form.department"
            :items="departments"
            item-text="name"
            item-value="id"
            :label="$t('Departement')"
            dense
            outlined
            clearable
          />

          <v-row dense class="mt-2">
            <!-- Type de carburant (seulement si non défini) -->
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="form.product"
                :items="products"
                item-text="name"
                item-value="id"
                :disabled="hasExistingProduct"
                :rules="hasExistingProduct ? [] : [required]"
                :label="$t('Type de Carburant')"
                dense
                outlined
                clearable
                :hint="hasExistingProduct ? $t('Déjà défini, non modifiable ici') : ''"
                persistent-hint
              />
            </v-col>

            <!-- Capacité réservoir (seulement si non définie) -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.reservoir_capacity"
                type="number"
                :rules="hasExistingCapacity ? [] : [isNumber]"
                :label="$t('Capacité reservoir')"
                dense
                outlined
                clearable
                persistent-hint
              />
            </v-col>
          </v-row>

          <v-divider class="my-4" />

          <!-- Switchs -->
          <v-row dense>
            <v-col cols="12" md="6">
              <v-switch
                inset
                color="success"
                v-model="form.is_kmh"
                :label="$t('Renseigner le kilometrage pendant le service ? ') + (form.is_kmh ? 'Oui' : 'Non')"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-switch
                inset
                color="primary"
                v-model="form.can_take_picture"
                :label="$t('Utiliser la photo du véhicule au service ? ') + (form.can_take_picture ? 'Oui' : 'Non')"
              />
            </v-col>
          </v-row>

          <!-- Kilométrage (visible seulement si is_kmh est activé) -->
          <v-row dense v-if="form.is_kmh">
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.kmh"
                type="number"
                :rules="[isNumber]"
                :label="$t('Kilométrage (km)')"
                dense
                outlined
                clearable
              />
            </v-col>
          </v-row>

          <v-divider class="my-4" />

          <!-- Infos Conducteur + Code secret -->
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.driver_last_name"
                :label="$t('Nom du conducteur')"
                dense
                outlined
                clearable
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.driver_first_name"
                :label="$t('Prenom du conducteur')"
                dense
                outlined
                clearable
              />
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.driver_phone_number"
                :rules="rules.driver_phone_number"
                :label="$t('N° de conducteur')"
                dense
                outlined
                clearable
                @input="enforcePhoneFormat"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.secret_code"
                :rules="rules.secret_code"
                :label="$t('Code secret')"
                dense
                outlined
                clearable
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions class="justify-end">
        <v-btn text @click="$emit('input', false)">
          {{ $t('Annuler') }}
        </v-btn>
        <v-btn color="primary" :loading="loading" dark @click="save">
          <v-icon left>mdi-content-save</v-icon>
          {{ $t('commons.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ProductDetailService, DepartmentService, CompanyVehiclesService } from "@/services"
import { required, isNumber, max4, phone } from "@/utils/validator"
export default {
  name: "QuickVehicleEditDialog",
  props: {
    value: { type: Boolean, default: false },   // v-model
    // Prop non requise, valeur par défaut => évite le warning "Expected Object, got Null"
    vehicle: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      isValid: false,
      loading: false,
      products: [],
      departments: [],
      form: {
        department: null,
        product: null,
        reservoir_capacity: null,
        is_kmh: false,
        driver_last_name: "",
        driver_first_name: "",
        driver_phone_number: "",
        secret_code: "",
        can_take_picture: false,
        kmh: null, // nouveau champ
      },
      rules: {
        registration_number: [required],
        vehicle_type: [required],
        department: [],
        make: [required],
        model: [required],
        carte_grise: [required],
        reservoir_capacity: [isNumber],
        year: [isNumber, max4],
        driver_phone_number:[phone],
        secret_code: [required, max4]
      },
      required: v => !!v || this.$t("messages.validation.required"),
      isNumber: v =>
        (v === null || v === "" || !isNaN(Number(v))) ||
        this.$t("messages.validation.number")
    }
  },
  computed: {
    hasExistingProduct() {
      return !!this.vehicle?.product
    },
    hasExistingCapacity() {
      const v = this.vehicle?.reservoir_capacity
      return v !== null && v !== undefined && String(v) !== "" && Number(v) > 0
    },
    company() {
      return this.$store.state.app.company || {}
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(open) {
        if (open) {
          this.hydrateFromVehicle()
          this.fetchLookups()
        }
      }
    }
  },
  methods: {
    hydrateFromVehicle() {
      const v = this.vehicle || {}
      this.form = {
        department: v.department ?? v.department_object?.id ?? null,
        product: v.product ?? null,
        reservoir_capacity: v.reservoir_capacity ?? null,
        is_kmh: !!v.is_kmh,
        driver_last_name: v.driver_last_name || "",
        driver_first_name: v.driver_first_name || "",
        driver_phone_number: v.driver_phone_number || "",
        secret_code: v.secret_code || "",
        can_take_picture: !!v.can_take_picture,
        kmh: v.kmh !== undefined && v.kmh !== null && v.kmh !== ""
          ? Number(v.kmh)
          : null,
      }
    },
    async fetchLookups() {
      try {
        const [prods, depts] = await Promise.all([
          ProductDetailService.getprincipalAll(),
          DepartmentService.getDepartment(this.company.pk)
        ])
        this.products = prods?.results || []
        this.departments = depts || []
      } catch (e) {
        // soft fail, on ne bloque pas la modale
      }
    },
    enforcePhoneFormat() {
      if (!this.form.driver_phone_number) return
      if (!this.form.driver_phone_number.startsWith("+225")) {
        this.form.driver_phone_number =
          "+225" + this.form.driver_phone_number.replace(/^(\+225)?/, "")
      }
    },
    async save() {
      const ok = await this.$refs.form.validate()
      if (!ok) return

      // Champs autorisés à être modifiés via cette modale
      const allowed = [
        "department",
        "is_kmh",
        "driver_last_name",
        "driver_first_name",
        "driver_phone_number",
        "secret_code",
        "can_take_picture",
        "reservoir_capacity",
        "kmh", // nouveau
      ]
      if (!this.hasExistingProduct) allowed.push("product")

      const payload = {}
      for (const k of allowed) {
        let val = this.form[k]

        if (k === "reservoir_capacity") {
          payload[k] =
            val === null || val === "" ? null : Number(val)
          continue
        }

        if (k === "kmh") {
          // si l’option is_kmh est désactivée, on peut remettre kmh à null
          if (!this.form.is_kmh) {
            payload[k] = null
          } else if (val !== null && val !== "") {
            payload[k] = Number(val)
          }
          continue
        }

        if (val !== undefined) {
          payload[k] = val
        }
      }

      this.loading = true
      try {
        let resp
        if (typeof CompanyVehiclesService.partialUpdate === "function") {
          // PATCH si disponible
          resp = await CompanyVehiclesService.partialUpdate(
            this,
            this.vehicle.id,
            payload
          )
        } else {
          // fallback PUT
          resp = await CompanyVehiclesService.update(
            this,
            this.vehicle.id,
            payload
          )
        }

        this.$store.commit("app/message", {
          msg: this.$t("messages.success.updated", {
            entity: this.$tc("vehicule", 1)
          }),
          color: "success"
        })
        this.$emit("saved", resp)
        this.$emit("input", false)
      } catch (err) {
        let message = ""
        for (const e in (err?.response?.data || {})) {
          message += `- ${e} : ${err.response.data[e]}\n`
        }
        this.$store.commit("app/message", {
          msg: message || this.$t("messages.error.unknown"),
          color: "error"
        })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
